import { Box, Alert, Text, PageBlock, Page } from 'braid-design-system';
import queryString from 'query-string';
import { Fragment, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import Redirect from 'src/components/Redirect/Redirect';
import { SiteWideFooter } from 'src/components/SiteWideFooter';
import Routes from 'src/config/routes';
import { useAuth } from 'src/hooks/auth';
import { useFetch } from 'src/hooks/fetch';
import type { StoreProps } from 'src/store';
import {
  getAccountData,
  getAccountFetchStatus,
  selectUserAccountType,
} from 'src/store/account';
import { genericError } from 'src/utils/api';
import NotFound from 'src/views/NotFound/NotFound';

interface AuthenticatedContainerProps {
  children: ReactNode;
}

const AuthenticatedContainer = ({ children }: AuthenticatedContainerProps) => {
  const { authLoaded } = useAuth();

  if (!authLoaded) {
    return (
      <Page footer={<SiteWideFooter />} footerPosition="belowFold">
        <CustomLoader />
      </Page>
    );
  }

  return <Authenticated>{children}</Authenticated>;
};

export default AuthenticatedContainer;

interface AuthenticatedProps {
  children: ReactNode;
}

const Authenticated = ({ children }: AuthenticatedProps) => {
  const location = useLocation();
  const account = useSelector((store: StoreProps) => store.account);
  const userAccountType = useSelector(selectUserAccountType);

  const { hasLoaded: accountLoaded } = useFetch(
    getAccountFetchStatus,
    getAccountData,
  );
  const { getAuthToken } = useAuth();
  if (!accountLoaded) {
    return (
      <Page footer={<SiteWideFooter />} footerPosition="belowFold">
        <CustomLoader />
      </Page>
    );
  } else if (account.loginVerified && getAuthToken()) {
    if (userAccountType === 'PrivateSeller') {
      return <Fragment>{children}</Fragment>;
    }
    return (
      <Page footer={<SiteWideFooter />} footerPosition="belowFold">
        <NotFound />
      </Page>
    );
  } else if (account.code === 401) {
    return (
      <Redirect
        path={queryString.stringifyUrl({
          url: Routes.SIGN_IN_URL,
          query: {
            ReturnUrl: location.pathname + location.search,
          },
        })}
      />
    );
  }
  return (
    <Page footer={<SiteWideFooter />} footerPosition="belowFold">
      <Error />
    </Page>
  );
};

const Error = () => (
  <Box paddingTop="xlarge">
    <PageBlock width="small">
      <Alert tone="critical">
        <Text>{genericError('verify your account')}</Text>
      </Alert>
    </PageBlock>
  </Box>
);
